import { useEffect, useState } from 'react';
import * as Yup from 'yup';
import classnames from 'classnames';
import { useFormik } from 'formik';
import { Trans, useTranslation } from 'react-i18next';
import Analytics from '@/analyticsGA';
import { useDebounce } from '@scramble/hooks';
import { Button, InlineAlert, InputField } from '@scramble/ui';
import { useApplyReferralCode } from '@/query/queries/referral/useApplyReferralCode';
import IconDiscount from '@scramble/design-system/icons/ticket-01.svg?react';
import { clearTempUserData } from '@/pages/AuthPage/utils';
import { useUserStore } from '@/store/userStore';
import { prettyFloatMoney } from '@/helpers/utils';
import s from './index.module.scss';

export const StepReferral = ({ nextStep }) => {
  const { t } = useTranslation();
  const [delayActive, setDelayActive] = useState(true);
  const accessToken = useUserStore(state => state.accessToken);
  const { mutateAsync: applyReferralCode, isPending, data } =
    useApplyReferralCode();

  useEffect(() => {
    Analytics.authRefCodeStep();
  }, []);

  useEffect(() => {
    if (!accessToken) {
      clearTempUserData();
      window.location.reload();
    }
  }, [accessToken]);

  const onSubmit = async (values, formik) => {
    formik.setSubmitting(true);
    const res = await applyReferralCode(values.referralCode);
    if (res?.is_activated) {
      Analytics.authRefCodeSubmit();
    } else {
      formik.setFieldError(
        'isValid',
        res?.message || t('validation:ref_code_is_invalid'),
      );
    }
    formik.setSubmitting(false);
  };

  const { values, errors, touched, isSubmitting, handleChange, handleSubmit } =
    useFormik({
      validationSchema: Yup.object({
        referralCode: Yup.string()
          .min(5, t('validation:ref_code_is_invalid'))
          .max(255, t('validation:ref_code_is_invalid'))
          .required(t('validation:ref_code_is_required')),
      }),
      initialValues: {
        referralCode: '',
      },
      onSubmit,
    });

  const debouncedValues = useDebounce(values.referralCode, 1000);

  useEffect(() => {
    debouncedValues && debouncedValues.length > 4 && handleSubmit();
  }, [debouncedValues, handleSubmit]);

  useEffect(() => {
     setDelayActive(!!values.referralCode)
  }, [values.referralCode]);

  useEffect(() => {
    setDelayActive(isPending);
  }, [isPending]);

  const isActivated = data?.is_activated;

  if (!accessToken) return null;

  return (
    <form className={s.form} onSubmit={handleSubmit}>
      <div className={s.header}>
        <h1 className="f-headline-sm f-500">{t('auth:step_referral.title')}</h1>
        <p className="f-body-md">{t('auth:step_referral.description')}</p>
      </div>
      <div className={s.group}>
        <InputField
          label={
            <Trans
              ns="auth"
              i18nKey="step_referral.label"
              components={{
                Span: <span className={s.label} />,
                InnerSpan: <span className="f-400" />,
              }}
            />
          }
          leftContainer={({ focused }) => (
            <IconDiscount
              className={classnames(s.icon, focused && s.icon_focused)}
            />
          )}
          name="referralCode"
          value={values.referralCode}
          onChange={handleChange}
          placeholder={t('auth:step_referral.placeholder')}
          disabled={isActivated || isSubmitting}
        />

        {isActivated ? (
          <InlineAlert
            type="success"
            title={t('auth:step_referral.code_successfully_applied')}
            message={t('auth:step_referral.bonus_activation_message', {
              amount: prettyFloatMoney(data.amount),
            })}
          />
        ) : (
          ((touched.referralCode && errors.referralCode) || errors.isValid) && (
            <InlineAlert
              type="danger"
              title={t('auth:step_referral.invalid_ref_code')}
              message={t('auth:step_referral.ref_code_no_longer_valid')}
            />
          )
        )}
        <Button
          type="button"
          theme="brand"
          appearance="filled"
          size="lg"
          disabled={delayActive || isPending}
          onClick={() => {
            !isActivated && Analytics.authRefCodeSkip();
            nextStep();
          }}
        >
          {t('auth:step_referral.continue')}
        </Button>
      </div>
    </form>
  );
};
